import * as RadixAccordion from "@radix-ui/react-accordion";
import clsx from "clsx";
import {
  forwardRef,
  HTMLAttributes,
  ElementRef,
  ComponentPropsWithoutRef,
  useState,
  useEffect,
  useRef,
} from "react";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore TS1479: ESM-only import in CommonJS context. See docs/coding-conventions/TYPESCRIPT.md
import { mergeRefs } from "react-merge-refs";
import classes from "./accordion.module.css";

export const Root = forwardRef<
  ElementRef<typeof RadixAccordion.Root>,
  Omit<RadixAccordion.AccordionSingleProps, "type"> & {
    variant?: "light" | "dark";
  }
>(({ className = "", variant = "light", ...props }, ref) => {
  return (
    <RadixAccordion.Root
      ref={ref}
      className={clsx([classes.root, classes[variant], className])}
      type="single"
      collapsible
      {...props}
    />
  );
});
// Accordion option for Sitecore Content Editor
// to be able to see all the accordions opened for editing
export const Multiple = forwardRef<
  ElementRef<typeof RadixAccordion.Root>,
  Omit<RadixAccordion.AccordionMultipleProps, "type">
>(({ className = "", ...props }, ref) => {
  return (
    <RadixAccordion.Root
      ref={ref}
      className={clsx([classes.root, className])}
      type="multiple"
      {...props}
    />
  );
});

export const Item = forwardRef<
  ElementRef<typeof RadixAccordion.Item>,
  ComponentPropsWithoutRef<typeof RadixAccordion.Item>
>(({ className = "", ...props }, ref) => {
  return (
    <RadixAccordion.Item
      ref={ref}
      className={clsx([classes.item, className])}
      {...props}
    />
  );
});

export const Header = forwardRef<
  ElementRef<typeof RadixAccordion.Header>,
  ComponentPropsWithoutRef<typeof RadixAccordion.Header>
>(({ className = "", ...props }, ref) => {
  return <RadixAccordion.Header ref={ref} className={className} {...props} />;
});

export const Trigger = forwardRef<
  ElementRef<typeof RadixAccordion.Trigger>,
  ComponentPropsWithoutRef<typeof RadixAccordion.Trigger>
>(({ className = "", ...props }, ref) => {
  return (
    <RadixAccordion.Trigger
      ref={ref}
      className={clsx([classes.trigger, className])}
      {...props}
    />
  );
});

export const TriggerIcon = forwardRef<
  HTMLSpanElement,
  HTMLAttributes<HTMLSpanElement>
>(({ className = "", ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={clsx([classes.triggerIcon, className])}
      {...props}
    />
  );
});

TriggerIcon.displayName = "AccordionTriggerIcon";

export const Content = forwardRef<
  ElementRef<typeof RadixAccordion.Content>,
  ComponentPropsWithoutRef<typeof RadixAccordion.Content>
>(({ className = "", children, ...props }, forwardedRef) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string>("0px");
  const [isOpen, setIsOpen] = useState(false);

  const localRef = useRef<HTMLDivElement>(null);

  const setRefs = mergeRefs([localRef, forwardedRef]);

  useEffect(() => {
    const element = localRef.current;
    const contentElement = contentRef.current;
    if (!element) return;

    const recalculateHeight = () => {
      const isOpenState = element.getAttribute("data-state") === "open";
      setIsOpen(isOpenState);
      if (isOpenState && contentElement) {
        setHeight(`${contentElement.scrollHeight}px`);
      } else {
        setHeight("0px");
      }
    };

    recalculateHeight();

    const mutationObserver = new MutationObserver(recalculateHeight);
    mutationObserver.observe(element, {
      attributes: true,
      attributeFilter: ["data-state"],
    });

    const resizeObserver = new ResizeObserver(recalculateHeight);
    if (contentElement) {
      resizeObserver.observe(contentElement);
    }

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <RadixAccordion.Content
      ref={setRefs}
      forceMount
      className={clsx(classes.content, className)}
      style={{
        maxHeight: height,
        opacity: isOpen ? 1 : 0,
        transition: "max-height 0.3s cubic-bezier(0.87,0,0.13,1), opacity 0.2s",
        overflow: "hidden",
      }}
      {...props}
    >
      <div ref={contentRef} className={classes.contentChild}>
        {children}
      </div>
    </RadixAccordion.Content>
  );
});
